import { httpInterceptor } from '../http-interceptor';

function unwrapList(payload) {
  if (!payload) return [];
  const body = payload.response ?? payload.Response;
  return Array.isArray(body) ? body : [];
}

/** Standard API envelope: { status, response, errorMessage } */
export function unwrapApiResponse(raw) {
  if (!raw) throw new Error('Empty response');
  const status = raw.status ?? raw.Status;
  const code = typeof status === 'string' ? Number.parseInt(status, 10) : Number(status);
  const body = raw.response ?? raw.Response;
  const err = raw.errorMessage ?? raw.ErrorMessage;
  if (code !== 200) {
    const msg = err?.message ?? err?.Message ?? 'Request failed';
    throw new Error(msg);
  }
  return body;
}

function norm(s) {
  return (s == null ? '' : String(s)).trim().toLowerCase();
}

function statusColors(status) {
  const s = status || '';
  const backgroundColor =
    s === 'Approved' ? '#d4edda'
      : s === 'Pending' ? '#f8d7da'
        : s === 'In Review' ? '#d1ecf1'
          : s === 'Cancelled' ? '#e2e3e5' : '#fff3cd';
  const textColor =
    s === 'Approved' ? '#155724'
      : s === 'Pending' ? '#721c24'
        : s === 'In Review' ? '#0c5460'
          : s === 'Cancelled' ? '#383d41' : '#856404';
  return { backgroundColor, textColor };
}

/** Fallback when User/entra-users fails or returns empty (Responsible Person / Assigned To). */
export const CR_ASSIGABLE_USERS_FALLBACK = [
  { userId: '1', displayName: 'J. Smith', departmentId: 3, departmentName: 'Operations' },
  { userId: '2', displayName: 'M. Davis', departmentId: 1, departmentName: 'Quality Assurance' },
  { userId: '3', displayName: 'A. Brown', departmentId: 4, departmentName: 'Finance' },
  { userId: '4', displayName: 'K. Patel', departmentId: 2, departmentName: 'IT Department' },
  { userId: '5', displayName: 'S. Lee', departmentId: 3, departmentName: 'Operations' },
  { userId: '6', displayName: 'T. Wong', departmentId: 1, departmentName: 'Quality Assurance' },
  { userId: '7', displayName: 'R. Kumar', departmentId: 2, departmentName: 'IT Department' },
  { userId: '8', displayName: 'L. Chen', departmentId: 4, departmentName: 'Finance' },
  { userId: '9', displayName: 'P. Johnson', departmentId: 3, departmentName: 'Operations' },
  { userId: '10', displayName: 'N. Williams', departmentId: 1, departmentName: 'Quality Assurance' },
];

export class ChangeRequestService {
  static ENDPOINTS = {
    GET_ALL: 'ChangeRequest/getallchangerequests',
    GET_BY_ID_PREFIX: 'ChangeRequest/getchangerequestbyid/',
    SAVE: 'ChangeRequest/savechangerequest',
    /** Microsoft Entra directory users ([Authorize]); plain JSON array, not ResponseDataModel. */
    ENTRA_USERS: 'User/entra-users',
    MASTER_DEPARTMENTS: 'ChangeRequest/master/departments',
    MASTER_RESOURCE_CATEGORIES: 'ChangeRequest/master/resourcecategories',
    MASTER_RISKS: 'ChangeRequest/master/risks',
    MASTER_OPPORTUNITIES: 'ChangeRequest/master/opportunities',
    MASTER_AFFECTED_PROCESSES: 'ChangeRequest/master/affectedprocesses',
  };

  static mapDummyUserFromApi(row) {
    const r = row || {};
    const id = r.userId ?? r.UserId;
    return {
      userId: id != null && id !== '' ? String(id) : '',
      displayName: r.displayName ?? r.DisplayName ?? '',
      departmentId: r.departmentId ?? r.DepartmentId ?? 0,
      departmentName: r.departmentName ?? r.DepartmentName ?? '',
    };
  }

  /** Map User/entra-users row (Id, DisplayName, Email, JobTitle) to assignable-user shape. */
  static mapEntraUserFromApi(row) {
    const r = row || {};
    const id = r.id ?? r.Id;
    const displayName = String(r.displayName ?? r.DisplayName ?? '').trim();
    const email = String(r.email ?? r.Email ?? '').trim();
    const jobTitle = r.jobTitle ?? r.JobTitle ?? '';
    const label = displayName || email || String(id ?? '');
    return {
      userId: id != null && id !== '' ? String(id) : '',
      displayName: label,
      departmentId: 0,
      departmentName: String(jobTitle || ''),
    };
  }

  static mapListItemFromApi(row) {
    const r = row || {};
    const id = r.changeRequestId ?? r.ChangeRequestId;
    const approvalsCount = r.approvalsApprovedCount ?? r.ApprovalsApprovedCount ?? 0;
    const approvalsTotal = r.approvalsTotalCount ?? r.ApprovalsTotalCount ?? 0;
    const status = r.status ?? r.Status ?? '';
    return {
      id,
      changeRequestId: id,
      crNumber: r.crNumber ?? r.CrNumber ?? '',
      title: r.title ?? r.Title ?? '',
      status,
      initiator: r.initiator ?? r.Initiator ?? '',
      approvalsCount,
      approvalsTotal,
      createdDate: r.createdDate ?? r.CreatedDate,
      updatedDate: r.updatedDate ?? r.UpdatedDate,
      ...statusColors(status),
    };
  }

  /** Map API detail (camelCase or PascalCase) to modal / Redux shape */
  static mapDetailToUi(d) {
    if (!d) return null;
    const id = d.changeRequestId ?? d.ChangeRequestId;
    if (id == null) return null;

    const resourceNeeded = d.resourceNeeded ?? d.ResourceNeeded ?? [];
    const resources = resourceNeeded.map(
      x => x.resourceText ?? x.ResourceText ?? '',
    );

    const respRows = d.responsibilities ?? d.Responsibilities ?? [];
    const responsibilities = respRows.map(r => {
      const role = r.roleName ?? r.RoleName ?? '';
      const person = r.personName ?? r.PersonName ?? '';
      return person ? `${role}: ${person}` : role;
    });

    const riskRows = d.risks ?? d.Risks ?? [];
    const oppRows = d.opportunities ?? d.Opportunities ?? [];
    const risks = [
      ...riskRows.map(r => ({
        type: 'Risk',
        description: r.riskName ?? r.RiskName ?? '',
        riskId: r.riskId ?? r.RiskId,
      })),
      ...oppRows.map(o => ({
        type: 'Opportunity',
        description: o.opportunityName ?? o.OpportunityName ?? '',
        opportunityId: o.opportunityId ?? o.OpportunityId,
      })),
    ];

    const procRows = d.affectedProcesses ?? d.AffectedProcesses ?? [];
    const affectedProcesses = procRows
      .map(p => p.processName ?? p.ProcessName ?? '')
      .filter(Boolean)
      .join(',');

    const partyRows = d.affectedParties ?? d.AffectedParties ?? [];
    const affectedDepartments =
      partyRows.length > 0
        ? partyRows.map(ap => ({
            department: ap.departmentName ?? ap.DepartmentName ?? '',
            responsiblePerson:
              ap.responsiblePersonName ?? ap.ResponsiblePersonName ?? '',
            responsiblePersonUserId:
              ap.responsiblePersonUserId ?? ap.ResponsiblePersonUserId ?? '',
          }))
        : [{ department: '', responsiblePerson: '', responsiblePersonUserId: '' }];

    const planRows = d.resourcePlan ?? d.ResourcePlan ?? [];
    const resourceDetails = planRows.map(r => ({
      id: r.changeRequestResourcePlanId ?? r.ChangeRequestResourcePlanId ?? `${Date.now()}-${Math.random()}`,
      category: r.categoryNameSnapshot ?? r.CategoryNameSnapshot ?? '',
      description: r.description ?? r.Description ?? '',
      estimatedCost: r.estimatedCost ?? r.EstimatedCost ?? '',
      assignedTo: r.assignedTo ?? r.AssignedTo ?? '',
    }));

    const apprRows = d.approvals ?? d.Approvals ?? [];
    const approvals = apprRows.map(a => ({
      department: a.departmentName ?? a.DepartmentName ?? '',
      responsiblePerson: a.responsiblePersonName ?? a.ResponsiblePersonName ?? '',
      responsiblePersonUserId:
        a.responsiblePersonUserId ?? a.ResponsiblePersonUserId ?? '',
      status: a.status ?? a.Status ?? 'Pending',
      comments: a.comments ?? a.Comments ?? '',
    }));

    const status = d.status ?? d.Status ?? '';
    const impactAssessment = {
      impactScope: d.impactScope ?? d.ImpactScope ?? '',
      impactLevel: d.impactLevel ?? d.ImpactLevel ?? 'Medium',
      affectedProcesses,
      affectedDepartments,
      customersImpacted: !!(d.customersImpacted ?? d.CustomersImpacted),
      regulatoryImpact: !!(d.regulatoryImpact ?? d.RegulatoryImpact),
      regulatoryDetails: d.regulatoryDetails ?? d.RegulatoryDetails ?? '',
    };

    return {
      id,
      changeRequestId: id,
      crNumber: d.crNumber ?? d.CrNumber ?? '',
      title: d.title ?? d.Title ?? '',
      status,
      initiator: d.initiator ?? d.Initiator ?? '',
      workflowStep: d.workflowStep ?? d.WorkflowStep,
      purpose: d.purpose ?? d.Purpose ?? '',
      qmsIntegrity: d.qmsIntegrity ?? d.QmsIntegrity ?? '',
      resources,
      responsibilities,
      risks,
      impactAssessment,
      resourceDetails,
      approvals,
      approvalsCount: approvals.filter(x => x.status === 'Approved').length,
      approvalsTotal: approvals.length,
      ...statusColors(status),
    };
  }

  /**
   * Build API save body from UI payload. Fetches masters to map names → ids.
   */
  static async buildApiSaveModel(uiPayload) {
    const masters = await this.fetchAllMasters();

    const riskByName = new Map(
      (masters.risks || []).map(r => [
        norm(r.riskName ?? r.RiskName),
        r.riskId ?? r.RiskId,
      ]),
    );
    const oppByName = new Map(
      (masters.opportunities || []).map(o => [
        norm(o.opportunityName ?? o.OpportunityName),
        o.opportunityId ?? o.OpportunityId,
      ]),
    );
    const procByName = new Map(
      (masters.affectedProcesses || []).map(p => [
        norm(p.processName ?? p.ProcessName),
        p.affectedProcessId ?? p.AffectedProcessId,
      ]),
    );
    const catByName = new Map(
      (masters.resourceCategories || []).map(c => [
        norm(c.categoryName ?? c.CategoryName),
        c.resourceCategoryId ?? c.ResourceCategoryId,
      ]),
    );

    const riskIds = [];
    const opportunityIds = [];
    for (const r of uiPayload.risks || []) {
      const desc = norm(r.description);
      if (!desc) continue;
      if (r.type === 'Opportunity') {
        const oid = oppByName.get(desc);
        if (oid != null) opportunityIds.push(oid);
      } else {
        const rid = riskByName.get(desc);
        if (rid != null) riskIds.push(rid);
      }
    }

    const impact = uiPayload.impactAssessment || {};
    const affectedProcessIds = (impact.affectedProcesses || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(name => procByName.get(norm(name)))
      .filter(id => id != null);

    const affectedParties = (impact.affectedDepartments || [])
      .filter(ap => ap && (ap.department || ap.responsiblePerson))
      .map(ap => ({
        departmentName: ap.department || '',
        responsiblePersonName: ap.responsiblePerson || null,
        responsiblePersonUserId: ap.responsiblePersonUserId || null,
      }));

    const resourcePlan = (uiPayload.resourceDetails || []).map(row => {
      const cat = row.category || '';
      const catId = cat ? catByName.get(norm(cat)) : null;
      return {
        resourceCategoryId: catId ?? null,
        categoryNameSnapshot: cat || null,
        description: row.description || null,
        estimatedCost: row.estimatedCost || null,
        assignedTo: row.assignedTo || null,
      };
    });

    const approvals = (uiPayload.approvals || []).map(a => ({
      departmentName: a.department || '',
      responsiblePersonName: a.responsiblePerson || null,
      responsiblePersonUserId: a.responsiblePersonUserId || null,
      status: a.status || 'Pending',
      comments: a.comments || null,
    }));

    const responsibilities = (uiPayload.responsibilities || []).map(r => {
      if (typeof r === 'string') {
        const idx = r.indexOf(':');
        if (idx === -1) return { roleName: r.trim(), personName: '' };
        return {
          roleName: r.slice(0, idx).trim(),
          personName: r.slice(idx + 1).trim(),
        };
      }
      return {
        roleName: r.role || r.roleName || '',
        personName: r.name || r.personName || '',
      };
    });

    const numericId = uiPayload.id ?? uiPayload.changeRequestId ?? 0;

    return {
      id: numericId,
      crNumber: uiPayload.crNumber || null,
      title: uiPayload.title || '',
      status: uiPayload.status || 'Pending',
      initiator: uiPayload.initiator || null,
      workflowStep: uiPayload.workflowStep ?? null,
      purpose: uiPayload.purpose || null,
      qmsIntegrity: uiPayload.qmsIntegrity || null,
      impactScope: impact.impactScope || null,
      impactLevel: impact.impactLevel || null,
      customersImpacted: !!impact.customersImpacted,
      regulatoryImpact: !!impact.regulatoryImpact,
      regulatoryDetails: impact.regulatoryDetails || null,
      resourceNeeded: (uiPayload.resources || []).filter(Boolean),
      responsibilities,
      riskIds,
      opportunityIds,
      affectedProcessIds,
      affectedParties,
      resourcePlan,
      approvals,
    };
  }

  static async getAllChangeRequests() {
    const raw = await httpInterceptor.post(this.ENDPOINTS.GET_ALL, {});
    const body = unwrapApiResponse(raw);
    const list = Array.isArray(body) ? body : [];
    return list.map(row => this.mapListItemFromApi(row));
  }

  static async getChangeRequestById(id) {
    const raw = await httpInterceptor.get(`${this.ENDPOINTS.GET_BY_ID_PREFIX}${id}`);
    const body = unwrapApiResponse(raw);
    return this.mapDetailToUi(body);
  }

  static async saveChangeRequest(uiPayload) {
    const body = await this.buildApiSaveModel(uiPayload);
    const raw = await httpInterceptor.post(this.ENDPOINTS.SAVE, body);
    return unwrapApiResponse(raw);
  }

  static async getMasterDepartments() {
    const res = await httpInterceptor.get(this.ENDPOINTS.MASTER_DEPARTMENTS);
    return unwrapList(res);
  }

  static async getMasterResourceCategories() {
    const res = await httpInterceptor.get(this.ENDPOINTS.MASTER_RESOURCE_CATEGORIES);
    return unwrapList(res);
  }

  static async getMasterRisks() {
    const res = await httpInterceptor.get(this.ENDPOINTS.MASTER_RISKS);
    return unwrapList(res);
  }

  static async getMasterOpportunities() {
    const res = await httpInterceptor.get(this.ENDPOINTS.MASTER_OPPORTUNITIES);
    return unwrapList(res);
  }

  static async getMasterAffectedProcesses() {
    const res = await httpInterceptor.get(this.ENDPOINTS.MASTER_AFFECTED_PROCESSES);
    return unwrapList(res);
  }

  static async fetchAllMasters() {
    const [departments, resourceCategories, risks, opportunities, affectedProcesses] =
      await Promise.all([
        this.getMasterDepartments(),
        this.getMasterResourceCategories(),
        this.getMasterRisks(),
        this.getMasterOpportunities(),
        this.getMasterAffectedProcesses(),
      ]);
    return {
      departments,
      resourceCategories,
      risks,
      opportunities,
      affectedProcesses,
    };
  }

  /** Responsible Person / Assigned To: GET User/entra-users (requires auth). */
  static async getDummyAssignableUsers() {
    const raw = await httpInterceptor.get(this.ENDPOINTS.ENTRA_USERS);
    const list = Array.isArray(raw) ? raw : [];
    const mapped = list.map(r => this.mapEntraUserFromApi(r)).filter(u => u.displayName);
    mapped.sort((a, b) =>
      a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }),
    );
    return mapped;
  }
}
